import React from 'react';

// =============================================
// SETTINGS SECTION COMPONENT
// =============================================

export function SettingsSection({
    // Auth & Sync
    authUser,
    lastSynced,
    syncStatus,
    isOnline,
    syncNow,
    handleSignOut,
    // Currency
    baseCurrency,
    setBaseCurrency,
    sgdRate,
    lkrRate,
    usdRate,
    setSgdRate,
    setLkrRate,
    setUsdRate,
    // Backups
    driveBackups,
    backupStatus,
    createBackup,
    restoreBackup,
    // Reports
    fiReportStatus,
    shareWithFI,
    exportAdvisorReportHTML,
    exportAdvisorReportPDF,
    exportHouseholdReportHTML,
    exportHouseholdReportPDF,
    // Data for display
    goldTotals,
    accountTotals,
    cardTotals,
    loanTotals,
    investmentTotals,
    fixedAssetTotals,
    monthlyIncomes,
    fmtBase,
    loadDriveBackups,
    shareAdvisorReport,
    advisorReportStatus,
    driveBackupsLoading,
    initialGoldInventory,
    initialPersonalLoans,
    initialGoldLoans,
    initialAccounts,
    setGoldInventory,
    setPersonalLoans,
    setGoldLoans,
    setAccounts,
    setCreditCards,
    setSgBudgetCycle1,
    setSgBudgetCycle2,
    setLkBudgetDamith,
    setLkBudgetShan,
    // Read-only data for backup/display
    goldInventory,
    personalLoans,
    goldLoans,
    accounts,
    creditCards,
    sgBudgetCycle1,
    sgBudgetCycle2,
    lkBudgetDamith,
    lkBudgetShan,
    baseCurrencyLabel,
    // Constants
    DRIVE_FOLDER_PATH,
    DRIVE_FILE_NAME,
}) {
    return (
        <div className="section-container">
            <div className="section-header">
                <div className="section-title">⚙️ Settings</div>
            </div>

            {/* Google Drive Sync Card */}
            <div className="sync-settings-card">
                <div className="sync-settings-header">
                    <div className="sync-settings-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#4285F4" />
                            <path d="M7 10l3.5 3.5L17 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div>
                        <div className="sync-settings-title">Google Drive Sync</div>
                        <div className="sync-settings-sub">Data synced to your personal Drive</div>
                    </div>
                </div>

                {authUser && (
                    <div className="sync-user-row">
                        <div className="sync-avatar">
                            {authUser.picture
                                ? <img src={authUser.picture} alt="" />
                                : authUser.name?.[0]?.toUpperCase() || 'U'
                            }
                        </div>
                        <div className="sync-user-info">
                            <div className="sync-user-name">{authUser.name}</div>
                            <div className="sync-user-email">{authUser.email}</div>
                        </div>
                    </div>
                )}

                {lastSynced && (
                    <div className="sync-last-time">
                        Last synced: {lastSynced} · Drive path: My Drive/{DRIVE_FOLDER_PATH.join('/')}/{DRIVE_FILE_NAME}
                    </div>
                )}

                <div className="sync-btn-row">
                    <button
                        className="btn-sync"
                        onClick={syncNow}
                        disabled={syncStatus === 'syncing' || !isOnline}
                    >
                        {syncStatus === 'syncing' ? '⏳ Syncing…' : '☁️ Sync Now'}
                    </button>
                    <button
                        className="btn-signout"
                        onClick={handleSignOut}
                    >
                        Sign Out
                    </button>
                </div>

                <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: '1.6' }}>
                    <div>• Auto-syncs 3 seconds after any change</div>
                    <div>• Stored at: My Drive/Application/iPlanner/iplanner-data-sync.json</div>
                    <div>• Folder IDs cached locally — no duplicate searches</div>
                    <div>• Tap the sync indicator in the header to force sync</div>
                </div>
            </div>
            <div className="summary-card" style={{ marginBottom: '20px' }}>
                <div className="summary-card-title">💱 Currency Settings</div>

                {/* Base Currency Selector */}
                <div className="form-group">
                    <label className="form-label">Base Currency</label>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '10px', lineHeight: '1.6' }}>
                        All dashboards, reports and AI FI exports will show totals in this currency. Data entry remains in each item's original currency.
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {['SGD', 'LKR', 'USD'].map(cur => (
                            <button
                                key={cur}
                                onClick={() => setBaseCurrency(cur)}
                                style={{
                                    flex: 1,
                                    padding: '12px 8px',
                                    borderRadius: '12px',
                                    border: baseCurrency === cur
                                        ? '2px solid var(--primary)'
                                        : '1px solid rgba(255,255,255,0.1)',
                                    background: baseCurrency === cur
                                        ? 'rgba(255,215,0,0.12)'
                                        : 'rgba(255,255,255,0.04)',
                                    color: baseCurrency === cur ? 'var(--primary)' : 'var(--text-secondary)',
                                    fontFamily: 'inherit',
                                    fontSize: '14px',
                                    fontWeight: baseCurrency === cur ? '700' : '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {cur === 'SGD' ? '🇸🇬 SGD' : cur === 'LKR' ? '🇱🇰 LKR' : '🇺🇸 USD'}
                            </button>
                        ))}
                    </div>
                    {/* Live preview */}
                    <div style={{
                        marginTop: '10px', padding: '10px 14px',
                        background: 'rgba(255,215,0,0.05)',
                        border: '1px solid rgba(255,215,0,0.15)',
                        borderRadius: '10px', fontSize: '12px',
                        color: 'var(--text-secondary)', lineHeight: '1.7',
                    }}>
                        <strong style={{ color: 'var(--primary)' }}>Preview:</strong>
                        {' '}Net worth shown as <strong style={{ color: 'var(--text-primary)' }}>{fmtBase(goldTotals.totalBase + accountTotals.totalBase - loanTotals.personalRemaining - loanTotals.goldOwed - cardTotals.totalBase)}</strong>
                        {' '}· Gold value <strong style={{ color: 'var(--text-primary)' }}>{fmtBase(goldTotals.totalBase)}</strong>
                        {' '}· Total loans <strong style={{ color: 'var(--text-primary)' }}>{fmtBase(loanTotals.personalRemaining + loanTotals.goldOwed)}</strong>
                    </div>
                </div>

                <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0 20px' }} />

                <div className="summary-card-title" style={{ marginBottom: '16px' }}>📐 Gold Rate & Exchange Rates</div>
                <div className="form-group">
                    <label className="form-label">SGD per gram (22K gold)</label>
                    <input
                        type="number"
                        className="form-input"
                        value={sgdRate || ''}
                        onChange={(e) => setSgdRate(e.target.value === '' ? '' : parseFloat(e.target.value))}
                        step="0.01"
                        placeholder="185.00"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">LKR per SGD</label>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                        Exchange rate: 1 SGD = ? LKR
                    </div>
                    <input
                        type="number"
                        className="form-input"
                        value={lkrRate || ''}
                        onChange={(e) => setLkrRate(e.target.value === '' ? '' : parseFloat(e.target.value))}
                        placeholder="220"
                    />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">SGD per USD</label>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                        Exchange rate: 1 USD = ? SGD
                    </div>
                    <input
                        type="number"
                        className="form-input"
                        value={usdRate || ''}
                        onChange={(e) => setUsdRate(e.target.value === '' ? '' : parseFloat(e.target.value))}
                        step="0.01"
                        placeholder="1.35"
                    />
                </div>
            </div>

            {/* Data Management */}
            <div className="summary-card" style={{ marginBottom: '20px' }}>
                <div className="summary-card-title">💾 Data Management</div>

                {/* Download Backup */}
                <button
                    className="btn btn-primary"
                    style={{ marginBottom: '12px' }}
                    onClick={() => {
                        const data = {
                            goldInventory,
                            personalLoans,
                            goldLoans,
                            accounts,
                            creditCards,
                            sgBudgetCycle1,
                            sgBudgetCycle2,
                            lkBudgetDamith,
                            lkBudgetShan,
                            sgdRate,
                            lkrRate,
                            usdRate,
                            baseCurrency,
                            exportDate: new Date().toISOString(),
                            version: '3.0'
                        };
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `iplanner-backup-${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                    }}
                >
                    📥 Download Backup
                </button>

                {/* Backup to Google Drive */}
                {authUser && (
                    <>
                        <button
                            className="btn-drive-backup"
                            onClick={createBackup}
                            disabled={backupStatus === 'backing-up' || !isOnline}
                        >
                            {backupStatus === 'backing-up' ? (
                                <>⏳ Saving to Drive…</>
                            ) : backupStatus === 'done' ? (
                                <>✅ Backup Saved to Drive</>
                            ) : backupStatus === 'error' ? (
                                <>❌ Backup Failed — Retry</>
                            ) : (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
                                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                    </svg>
                                    Backup to Google Drive
                                </>
                            )}
                        </button>

                        {/* Backup history list */}
                        <div className="backup-history-list">
                            <div className="backup-history-header">
                                <span>☁️ Drive Backups — Application/iPlanner/</span>
                                <span style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 600 }}
                                    onClick={loadDriveBackups}>
                                    {driveBackupsLoading ? '…' : '↻ Refresh'}
                                </span>
                            </div>
                            {driveBackupsLoading ? (
                                <div className="backup-empty">Loading backups…</div>
                            ) : driveBackups.length === 0 ? (
                                <div className="backup-empty">No backups yet — tap "Backup to Google Drive" to create one</div>
                            ) : (
                                driveBackups.map(file => {
                                    const d = new Date(file.createdTime);
                                    const dateStr = d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
                                    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                    const sizeKB = file.size ? `${(file.size / 1024).toFixed(1)} KB` : '';
                                    return (
                                        <div key={file.id} className="backup-history-item">
                                            <span className="backup-file-icon">🗂️</span>
                                            <div className="backup-file-info">
                                                <div className="backup-file-name">{file.name}</div>
                                                <div className="backup-file-date">{dateStr} at {timeStr}{sizeKB ? ` · ${sizeKB}` : ''}</div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            Each backup is a separate timestamped file. Manage backups directly in Google Drive.
                        </div>
                    </>
                )}

                <div style={{ height: '1px', background: 'var(--border)', margin: '16px 0' }}></div>

                {/* ── Share with Financial Instructor ── */}
                {authUser && (
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>🤖 AI Financial Instructor</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: '1.6' }}>
                                Exports a sanitized snapshot to Google Drive — all names, account numbers, card details and bank identifiers are stripped before upload.
                            </div>
                        </div>
                        <button
                            className="btn-fi-share"
                            onClick={shareWithFI}
                            disabled={fiReportStatus === 'uploading' || !isOnline}
                        >
                            {fiReportStatus === 'uploading' ? (
                                <>⏳ Uploading to Drive…</>
                            ) : fiReportStatus === 'done' ? (
                                <>✅ Shared — iplanner-fi-report.json</>
                            ) : fiReportStatus === 'error' ? (
                                <>❌ Upload Failed — Retry</>
                            ) : (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                        <polyline points="16 6 12 2 8 6" />
                                        <line x1="12" y1="2" x2="12" y2="15" />
                                    </svg>
                                    Share With FI
                                </>
                            )}
                        </button>
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '6px', marginBottom: '4px' }}>
                            📁 Saves to: Application/iPlanner/<strong style={{ color: 'var(--text-secondary)' }}>iplanner-fi-report.json</strong> — overwrites on each share.
                        </div>

                        {/* Advisor Report HTML export */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                            <button
                                className="btn-fi-share"
                                style={{ flex: 1, minWidth: '140px', background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(99,102,241,0.14) 100%)', borderColor: 'rgba(139,92,246,0.4)' }}
                                onClick={exportAdvisorReportHTML}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                                Advisor Report (HTML)
                            </button>
                            <button
                                className="btn-fi-share"
                                style={{ flex: 1, minWidth: '140px', background: 'linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(220,38,38,0.14) 100%)', borderColor: 'rgba(239,68,68,0.4)' }}
                                onClick={exportAdvisorReportPDF}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                                Advisor Report (PDF)
                            </button>
                            <button
                                className="btn-fi-share"
                                style={{ flex: 1, minWidth: '140px' }}
                                onClick={shareAdvisorReport}
                                disabled={advisorReportStatus === 'uploading' || !isOnline}
                            >
                                {advisorReportStatus === 'uploading' ? <>⏳ Uploading…</> :
                                    advisorReportStatus === 'done' ? <>✅ Advisor Report Saved</> :
                                        advisorReportStatus === 'error' ? <>❌ Upload Failed</> : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
                                                Advisor Report (Drive)
                                            </>
                                        )}
                            </button>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '6px', marginBottom: '4px' }}>
                            📄 HTML: opens printable report in new tab &nbsp;·&nbsp; 📁 Drive: saves <strong style={{ color: 'var(--text-secondary)' }}>iplanner-fi-report-advisor.json</strong>
                        </div>
                        <div style={{ height: '1px', background: 'var(--border)', margin: '16px 0' }}></div>
                    </>
                )}

                {/* Household Report */}
                {authUser && (
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>🏠 Household Report</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: '1.6' }}>
                                Family-friendly financial summary — real figures with loan/gold labels, but no account numbers or bank names. Share with your spouse to stay in sync.
                            </div>
                        </div>
                        <button
                            className="btn-fi-share"
                            style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(5,150,105,0.14) 100%)', borderColor: 'rgba(16,185,129,0.4)' }}
                            onClick={exportHouseholdReportHTML}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                            Family Financial Summary (HTML)
                        </button>
                        <button
                            className="btn-fi-share"
                            style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(220,38,38,0.14) 100%)', borderColor: 'rgba(239,68,68,0.4)', marginTop: '8px' }}
                            onClick={exportHouseholdReportPDF}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                            Family Financial Summary (PDF)
                        </button>
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '6px', marginBottom: '4px' }}>
                            Opens a printable report in a new tab — net worth, loans, gold, budget, scores &amp; action items.
                        </div>
                        <div style={{ height: '1px', background: 'var(--border)', margin: '16px 0' }}></div>
                    </>
                )}

                {/* Import Backup */}
                <input
                    type="file"
                    id="import-file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const reader = new FileReader();
                        reader.onload = (event) => {
                            try {
                                const importedData = JSON.parse(event.target.result);

                                // Validate data structure
                                if (!importedData.goldInventory && !importedData.personalLoans && !importedData.goldLoans && !importedData.accounts) {
                                    alert('Invalid backup file format');
                                    return;
                                }

                                // Confirm import
                                if (confirm('This will replace all current data with the backup. Continue?')) {
                                    setGoldInventory(importedData.goldInventory || []);
                                    setPersonalLoans(importedData.personalLoans || []);
                                    setGoldLoans(importedData.goldLoans || []);
                                    setAccounts(importedData.accounts || []);
                                    setCreditCards(importedData.creditCards || []);
                                    setSgBudgetCycle1(importedData.sgBudgetCycle1 || []);
                                    setSgBudgetCycle2(importedData.sgBudgetCycle2 || []);
                                    setLkBudgetDamith(importedData.lkBudgetDamith || []);
                                    setLkBudgetShan(importedData.lkBudgetShan || []);
                                    setSgdRate(importedData.sgdRate || 186);
                                    setLkrRate(importedData.lkrRate || 245);
                                    setUsdRate(importedData.usdRate || 1.26);
                                    setBaseCurrency(importedData.baseCurrency || 'SGD');
                                    alert('✅ Backup imported successfully!');
                                }
                            } catch (error) {
                                alert('❌ Error reading backup file: ' + error.message);
                            }
                        };
                        reader.readAsText(file);
                        e.target.value = ''; // Reset input
                    }}
                />
                <button
                    className="btn btn-secondary"
                    style={{ marginBottom: '12px' }}
                    onClick={() => document.getElementById('import-file').click()}
                >
                    📤 Import Backup
                </button>

                {/* Reset All Data */}
                <button
                    className="btn btn-danger"
                    onClick={() => {
                        const confirmText = 'DELETE ALL DATA';
                        const userInput = prompt(
                            `⚠️ WARNING: This will permanently delete ALL your data!\n\n` +
                            `• ${goldInventory.length} Gold Items\n` +
                            `• ${personalLoans.length} Personal Loans\n` +
                            `• ${goldLoans.length} Gold Loans\n` +
                            `• ${accounts.length} Bank Accounts\n\n` +
                            `Type "${confirmText}" to confirm:`
                        );

                        if (userInput && userInput.trim().toUpperCase() === confirmText) {
                            setGoldInventory(initialGoldInventory);
                            setPersonalLoans(initialPersonalLoans);
                            setGoldLoans(initialGoldLoans);
                            setAccounts(initialAccounts);
                            setCreditCards([]);
                            setSgBudgetCycle1([]);
                            setSgBudgetCycle2([]);
                            setLkBudgetDamith([]);
                            setLkBudgetShan([]);
                            setSgdRate(186);
                            setLkrRate(245);
                            setUsdRate(1.26);
                            localStorage.removeItem('iPlannerData_v3');
                            alert('✅ All data has been reset to defaults');
                        } else if (userInput !== null) {
                            alert('❌ Reset cancelled - confirmation text did not match');
                        }
                    }}
                >
                    🗑️ Reset All Data
                </button>

                <div style={{
                    marginTop: '20px',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '12px',
                    fontSize: '11px',
                    color: 'var(--text-tertiary)',
                    lineHeight: '1.6'
                }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        💡 Data Storage Info
                    </div>
                    <div>• Data is saved locally in your browser</div>
                    <div>• Regular backups recommended</div>
                    <div>• Clearing browser data will erase all records</div>
                </div>
            </div>

            {/* App Info */}
            <div className="summary-card">
                <div className="summary-card-title">ℹ️ Application Info</div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                    fontSize: '13px'
                }}>
                    <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '4px' }}>VERSION</div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>3.0.0</div>
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '4px' }}>BASE CURRENCY</div>
                        <div style={{ color: 'var(--primary)', fontWeight: 600 }}>{baseCurrencyLabel}</div>
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '4px' }}>STORAGE</div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Local + Drive</div>
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '4px' }}>GOLD ITEMS</div>
                        <div style={{ color: 'var(--primary)', fontWeight: 600 }}>{goldInventory.length}</div>
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '4px' }}>TOTAL LOANS</div>
                        <div style={{ color: 'var(--secondary)', fontWeight: 600 }}>{personalLoans.length + goldLoans.length}</div>
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '4px' }}>ACCOUNTS</div>
                        <div style={{ color: 'var(--primary)', fontWeight: 600 }}>{accounts.length}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}